import { ENUM__html_ref_stub } from '@/enums/ENUM__html_ref_stub';
import { use_effect } from '@/hooks/_base/use_effect';
import { use_ref_with_fallback } from '@/hooks/_base/use_ref_with_fallback';
import { use_hint_shortcut } from '@/hooks/hints_shortcut/use_hint_shortcut';
import { assert_true, assert_type_enum } from '@shared/asserts/utils_asserts';
import { EShortcutType } from '@shared/dto/hint_shortcuts/e_shortcut_type';
import { TutorialProgressKey } from '@shared/types/types';
import { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { EScreenZone } from './e_screen_zone';

interface Props {
  hint_shortcut_keys?: EShortcutType[];
  tutorial_toasts_keys?: TutorialProgressKey[];
  children: React.ReactNode;
}

/*
 * CONSTANTS
 */

/*
 * IMPLEMENTATION
 */

export const ViewShortcutTrigger = forwardRef<HTMLDivElement, Props>(({ children, hint_shortcut_keys = [], tutorial_toasts_keys = [] }, ref) => {
  assert_true(!hint_shortcut_keys.is_empty() || !tutorial_toasts_keys.is_empty());
  hint_shortcut_keys.forEach(key => assert_type_enum(key, EShortcutType));

  const tutorial_progresses_hints = hint_shortcut_keys.map(hint_key => use_hint_shortcut(hint_key));

  const ref_trigger = use_ref_with_fallback<HTMLDivElement>(ENUM__html_ref_stub.DIV);
  const ref_hide_timeout_id = use_ref_with_fallback<number>(NaN);

  const [is_mouse_inside, set_is_mouse_inside] = useState<boolean>(false);

  const zone_trigger = useMemo(() => get_zone_trigger(), [ref_trigger.current]);

  /*
   * EFFECTS
   */
  use_effect(() => {
    return () => cleanup();
  });

  /*
   * EVENTS
   */

  function onMouseOver(e: React.MouseEvent) {
    if (e.relatedTarget && is_mouse_inside_component(e.relatedTarget)) return;

    clearTimeout(ref_hide_timeout_id.current);
    set_is_mouse_inside(true);

    const target_current = ref_trigger.current;
    const target_related = e.relatedTarget as Node | null;

    if (target_current && (!target_related || !target_current.contains(target_related))) {
      const zone_target = zone_trigger === EScreenZone.TOP ? EScreenZone.BOTTOM : EScreenZone.TOP;
      show_hints_shortcut_all(zone_target);

      start_hint_shortcut_timer(zone_target);
    }
  }

  function onMouseLeave(e: React.MouseEvent<HTMLDivElement>) {
    if (e.relatedTarget && is_mouse_inside_component(e.relatedTarget)) return;

    set_is_mouse_inside(false);
    try_hide_hint_shortcuts_all();
  }

  /*
   * METHODS
   */

  function get_zone_trigger() {
    const rect = ref_trigger.current.getBoundingClientRect();
    const trigger_center_Y = rect.top + rect.height / 2;
    const middle_screen_line = window.innerHeight / 2;

    return trigger_center_Y < middle_screen_line ? EScreenZone.TOP : EScreenZone.BOTTOM;
  }

  function cleanup() {
    set_is_mouse_inside(false);
    try_hide_hint_shortcuts_all();
    clearTimeout(ref_hide_timeout_id.current);
  }

  function is_mouse_inside_component(related_target: EventTarget) {
    if (!(related_target instanceof HTMLElement)) return false;

    let out = false;
    let parent = related_target.parentElement;

    while (parent) {
      if (parent === ref_trigger.current) {
        out = true;
        break;
      }
      parent = parent.parentElement;
    }

    return out;
  }

  function start_hint_shortcut_timer(zone_target: EScreenZone) {
    if (!is_mouse_inside) return;

    show_hints_shortcut_all(zone_target);
  }

  function try_hide_hint_shortcuts_all() {
    tutorial_progresses_hints.forEach(item => item.try_hide_hint_shortcut());
  }

  function show_hints_shortcut_all(zone_target: EScreenZone) {
    tutorial_progresses_hints.forEach(item => item.show_hint_shortcut_in_zone(zone_target));
  }

  useImperativeHandle(ref, () => ref_trigger.current as HTMLDivElement);

  /*
   * HTML
   */

  return (
    <div {...{ onMouseOver, onMouseLeave }} ref={ref_trigger} className="contents">
      {children}
    </div>
  );
});
